/**
 * InsightsStreamingHelper
 *
 * Extracted from ChatbotexpandComponent to keep the component leaner. These are the
 * insights/QnA streaming post-processing and auto-scroll utilities. The component keeps
 * thin wrapper methods that delegate here (passing `this` as `comp`), so all existing
 * call sites — including template bindings — remain unchanged and behavior is preserved.
 */
export class InsightsStreamingHelper {
  static extractTextFromStreamEvent(comp: any, event: any): string {
    if (!event) return '';
    if (event.event === 'text_delta') {
      return event.data?.delta || '';
    } else if (typeof event.data === 'string') {
      return event.data;
    } else if (event.data) {
      let text = event.data.delta || event.data.chunk || event.data.Value || (typeof event.data === 'string' ? event.data : '');
      if (!text && event.data.insights && event.data.insights.Value) {
        text = event.data.insights.Value;
      }
      return text;
    }
    return '';
  }

  static cleanStreamingArtifacts(comp: any, text: string): string {
    let cleaned = text;

    cleaned = cleaned.replace(/data:\s*/gi, '');
    cleaned = cleaned.replace(/event:\s*\w+/gi, '');

    // Fix Unicode character encoding issues common in streaming
    cleaned = cleaned.replace(/â€œ/g, '"');  // Opening quote
    cleaned = cleaned.replace(/â€/g, '"');   // Closing quote  
    cleaned = cleaned.replace(/â€'/g, '—');  // Em dash
    cleaned = cleaned.replace(/â€'/g, '-');  // En dash

    // Fix Unicode character encoding issues common in streaming

    // Fix spacing between numbers and words (e.g., "past12months" → "past 12 months")
    cleaned = cleaned.replace(/([a-z])(\d+)/gi, '$1 $2');
    cleaned = cleaned.replace(/(\d+)([a-z])/gi, '$1 $2');

    // Fix hyphenated or dashed words that got spaces (e.g., "12 -month" → "12-month")
    cleaned = cleaned.replace(/(\d+)\s*-\s*([a-z]+)/gi, '$1-$2');

    // Fix both literal string "\n" and encoded "\\n" artifacts
    cleaned = cleaned.replace(/\\n/g, '\n');

    // Collapse multiple horizontal spaces only (preserve newlines for list splitting)
    cleaned = cleaned.replace(/[ \t]+/g, ' ');

    return cleaned;
  }

  static updateInsightsPopupRealTime(comp: any): void {
    try {
      if (!comp.isInsightspopupOpen) {
        comp.isInsightspopupOpen = true;
      }

      const currentText = Array.isArray(comp.insightsVal) && comp.insightsVal.length > 0
        ? comp.insightsVal[0]
        : '';

      comp.InsightsResponse = {
        header: 'Insights (Streaming...)',
        message: currentText,
        show: true,
      };
      comp._insightsSerive.showInisghts(comp.InsightsResponse);

    } catch (error) {
    }
  }

  static updateInsightsPopupFinal(comp: any): void {
    try {
      comp.InsightsResponse = {
        header: 'Insights',
        message: Array.isArray(comp.insightsVal) ? comp.insightsVal.join('\n') : String(comp.insightsVal),
        show: true,
      };

      comp._insightsSerive.showInisghts(comp.InsightsResponse);

    } catch (error) {
    }
  }

  static scrollInsightsDownSmooth(comp: any): void {
    try {
      comp.stopInsightsAutoScroll();

      const activeIndex = comp.chatPreviewBlocks.findIndex((b: any) => b.messageId === comp.selectedMessageId);
      const activeContainer = comp.insightsContainers?.toArray()[activeIndex];

      if (activeContainer && activeContainer.nativeElement) {
        const scrollableContainer = activeContainer.nativeElement.closest('.output-insights');

        if (scrollableContainer) {
          const currentScrollTop = scrollableContainer.scrollTop;
          const maxScrollTop = scrollableContainer.scrollHeight - scrollableContainer.clientHeight;

          if (maxScrollTop > 0 && currentScrollTop < maxScrollTop) {
            comp.isInsightsScrolling = true;
            const scrollStep = Math.max(2, Math.ceil((maxScrollTop - currentScrollTop) / 20));

            comp.insightsScrollInterval = setInterval(() => {
              const newScrollTop = scrollableContainer.scrollTop + scrollStep;

              if (newScrollTop >= maxScrollTop) {
                scrollableContainer.scrollTop = maxScrollTop;
                comp.stopInsightsAutoScroll();
              } else {
                scrollableContainer.scrollTop = newScrollTop;
              }
            }, 50);
          }
        }
      }
    } catch (error) {
      console.warn('Error during insights auto-scroll:', error);
      comp.stopInsightsAutoScroll();
    }
  }

  static scrollInsightsToTopSmooth(comp: any): void {
    try {
      comp.stopInsightsAutoScroll();

      const activeIndex = comp.chatPreviewBlocks.findIndex((b: any) => b.messageId === comp.selectedMessageId);
      const activeContainer = comp.insightsContainers?.toArray()[activeIndex];

      if (activeContainer && activeContainer.nativeElement) {
        const el = activeContainer.nativeElement;
        el.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    } catch (error) {
      console.warn('Error during insights scroll to top:', error);
    }
  }

  static stopInsightsAutoScroll(comp: any): void {
    if (comp.insightsScrollInterval) {
      clearInterval(comp.insightsScrollInterval);
      comp.insightsScrollInterval = null;
    }
    comp.isInsightsScrolling = false;
  }

  static updateMarkdownTextRealTime(comp: any, qnaResponse: string): void {
    try {
      if (qnaResponse && qnaResponse.trim() !== "") {
        let cleanedQnaText = comp.cleanQnaTextForParaOnly(qnaResponse);

        cleanedQnaText = cleanedQnaText
          .replace(/\s+/g, ' ')  // Collapse multiple spaces
          .replace(/^\s+|\s+$/g, '')  // Trim
          .trim();

        comp.updateQuestionAnswerForSelectedMessage(cleanedQnaText);
      }
    } catch (error) {
      console.warn('Error updating markdown text in real-time:', error);
    }
  }

  /**
   * Helper to strip markdown tables from QnA text, leaving only paragraphs.
   */
  static cleanQnaTextForParaOnly(comp: any, text: string): string {
    if (!text) return "";

    // Split by actual newlines or literal \n strings common in streams
    const lines = text.split(/\n|\\n/);

    return lines
      .filter((line: string) => {
        const trimmed = line.trim();
        // Ignore lines that look like markdown table rows or separators
        if (trimmed.startsWith('|') || (trimmed.startsWith('+-') && trimmed.endsWith('-+')) || (trimmed.startsWith('|-') && trimmed.endsWith('-|'))) {
          return false;
        }
        return true;
      })
      .join('\n')
      .trim();
  }

  static processInsightsChunkRealTime(comp: any, newChunk: string, fullResponse: string): void {
    try {
      comp.insightsStreamingText += newChunk;
      comp.insightsStreamingPoints = comp.formatInsightsIntoPoints(comp.insightsStreamingText);
      comp.updateInsightsPopupRealTime();
    } catch (error) {
    }
  }

  static processInsightsChunkFinal(comp: any, fullInsightsResponse: string): void {
    comp.isInsightsStreaming = false; // Streaming complete
    try {
      const rawInsightsForSaving = fullInsightsResponse;
      let parsedInsights: any;
      try {
        parsedInsights = JSON.parse(fullInsightsResponse);
      } catch (e) {
        parsedInsights = null;
      }

      if (parsedInsights && typeof parsedInsights === 'object') {
        let combinedInsightsText = '';
        let insightsFound = false;

        for (const section in parsedInsights) {
          if (parsedInsights[section] && parsedInsights[section].Value) {
            combinedInsightsText += parsedInsights[section].Value + '\n';
            insightsFound = true;
          }
        }

        if (insightsFound) {
          comp.insightsVal = comp.formatInsightsIntoPoints(combinedInsightsText);
        } else {
          comp.insightsVal = ["No Data Available"];
        }
      } else if (fullInsightsResponse && fullInsightsResponse.trim() !== "") {
        comp.insightsVal = comp.formatInsightsIntoPoints(fullInsightsResponse);
      } else {
        comp.insightsVal = ["No Data Available"];
      }
      comp.updateInsightsPopupFinal();

      setTimeout(() => {
        comp.scrollInsightsToTopSmooth();
      }, 500);

    } catch (error) {
      comp.insightsVal = ['Failed to process insights.'];
      comp.updateInsightsPopupFinal();

      setTimeout(() => {
        comp.scrollInsightsToTopSmooth();
      }, 300);
    }
  }

  static checkStreamCompletion(comp: any, 
    insightsCompleted: boolean,
    qnaCompleted: boolean,
    insightsResponse: string,
    qnaResponse: string,
    mcode: number,
    targetBlock?: any
  ): void {
    if (insightsCompleted && qnaCompleted) {
      let finalQnaText = 'No data Available';
      if (mcode === 1) {
        let snapshotQnaText = 'No data Available';
        try {
          if (qnaResponse && qnaResponse.trim() !== "") {
            snapshotQnaText = comp.cleanQnaTextForParaOnly(qnaResponse)
              .replace(/^[^:]+:\s*/, '')  // Remove prefixes like "Answer:"
              .replace(/\\n/g, ' ')  // Replace escaped newlines
              .replace(/\n/g, ' ')  // Replace actual newlines
              .trim();
          }
        } catch (e) {
          console.warn('Error processing snapshot QnA text:', e);
        }
        finalQnaText = snapshotQnaText;
        comp.updateQuestionAnswerForSelectedMessage(snapshotQnaText);
      } else {
        let processedQnaText = 'No data Available';
        try {
          if (qnaResponse && qnaResponse.trim() !== "") {
            processedQnaText = comp.cleanQnaTextForParaOnly(qnaResponse)
              .replace(/^[^:]+:\s*/, '')  // Remove prefixes like "Answer:"
              .replace(/\\n/g, ' ')  // Replace escaped newlines
              .replace(/\n/g, ' ')  // Replace actual newlines
              .trim();
          }
        } catch (e) {
          console.warn('Error processing crosstab/brand analysis QnA text:', e);
        }
        finalQnaText = processedQnaText;
        comp.updateQuestionAnswerForSelectedMessage(processedQnaText);
      }

      comp.isStreamFinished = true; // Mark insights stream as successfully finished!
      comp.statusQueue = []; // Clear queue to instantly show post-stream message
      comp._insightsSerive.showInisghts(comp.InsightsResponse);
      setTimeout(() => {
        comp.stopAiLoader();
        comp.arktabaccess = false;
      }, 1500);

      // Update the session cache for multi-view persistence BEFORE saving history
      const msgId = targetBlock?.messageId || comp.selectedMessageId || 'current';
      if (!comp.multiViewInsightsCache[msgId]) {
        comp.multiViewInsightsCache[msgId] = {};
      }
      if (!comp.multiViewInsightsCache[msgId][mcode]) {
        comp.multiViewInsightsCache[msgId][mcode] = {} as any;
      }
      comp.multiViewInsightsCache[msgId][mcode].insightsVal = comp.insightsVal;
      comp.multiViewInsightsCache[msgId][mcode].qnaText = finalQnaText;
      if (mcode !== 1) {
        comp.multiViewInsightsCache[msgId][mcode].rawInsights = insightsResponse;
      } else {
        comp.multiViewInsightsCache[msgId][mcode].standard_insights = comp.snapshotinsights_standard_text;
        comp.multiViewInsightsCache[msgId][mcode].trend_insights = comp.snapshotinsights_trendline_text;
        const combinedRaw = (comp.snapshotinsights_standard_text || '').trim() + "[[[SPLIT]]]" + (comp.snapshotinsights_trendline_text || '').trim();
        comp.multiViewInsightsCache[msgId][mcode].rawInsights = combinedRaw;
      }

      // Save history AFTER cache is populated so that getInsights is bundled into ES!
      const doSave = () => {
        comp.saveChatList(
          targetBlock ? targetBlock.question : (comp.chatPreviewBlocks.length > 0 ? comp.chatPreviewBlocks[comp.chatPreviewBlocks.length - 1].question : comp.inputValue),
          comp.chatResponse,
          finalQnaText,
          targetBlock?.messageId
        );
        comp.chatResponse = [];
        const innerMsgId = targetBlock?.messageId || comp.selectedMessageId || 'current';
        if (comp.inFlightInsights[innerMsgId]) {
          comp.inFlightInsights[innerMsgId].delete(mcode);
        }
      };

      if ((comp as any).pendingBackgroundFetches) {
        (comp as any).pendingBackgroundFetches.then(doSave).catch((err: any) => {
          console.error('[ORCHESTRATION] Error waiting for background fetches before save:', err);
          doSave();
        });
      } else {
        doSave();
      }
    }
  }

  static checkInsightsOverflow(comp: any, block: any, container: HTMLElement): void {
    if (container) {
      const isOverflowing = container.scrollHeight > container.clientHeight + 5;
      const isAtTop = container.scrollTop <= 5;
      const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 5;

      if (block.isInsightsOverflowing !== isOverflowing || block.isAtTop !== isAtTop || block.isAtBottom !== isAtBottom) {
        // Use Promise.resolve().then() to defer the state update to the microtask queue,
        // avoiding ExpressionChangedAfterItHasBeenCheckedError while keeping it fast.
        Promise.resolve().then(() => {
          block.isInsightsOverflowing = isOverflowing;
          block.isAtTop = isAtTop;
          block.isAtBottom = isAtBottom;
          comp.cdr.detectChanges();
        });
      }
    }
  }

  static formatInsightsIntoPoints(comp: any, text: any): string[] {
    if (!text) {
      return [];
    }

    let rawText = '';

    // Determine the raw source text based on data type
    if (typeof text === 'string') {
      rawText = text;
    } else if (typeof text === 'object') {
      // Handle the case where we've received or parsed an object structure
      // Case 1: Direct Value property (e.g. { Value: "..." })
      if (text.Value && typeof text.Value === 'string') {
        rawText = text.Value;
      }
      // Case 2: Nested insights (e.g. { insights: { Value: "..." } })
      else if (text.insights && text.insights.Value && typeof text.insights.Value === 'string') {
        rawText = text.insights.Value;
      }
      // Case 3: Keyed sections (common in original insights Response e.g. { "Section 1": { Value: "..." } })
      else {
        let combinedVal = '';
        let foundValue = false;

        for (const key in text) {
          if (text[key] && text[key].Value && typeof text[key].Value === 'string') {
            combinedVal += text[key].Value + '\n';
            foundValue = true;
          }
        }

        if (foundValue) {
          rawText = combinedVal;
        } else {
          // If no recognized structure, fallback to string representation
          try {
            rawText = JSON.stringify(text);
          } catch (e) {
            rawText = String(text);
          }
        }
      }
    } else {
      rawText = String(text);
    }

    // Handle dual-view snapshot insights (Standard vs Trend)
    if (rawText.includes('[[[SPLIT]]]')) {
      const parts = rawText.split('[[[SPLIT]]]');
      rawText = comp.isTrendActive ? (parts[1] || parts[0]) : parts[0];
    }

    // Safety check that rawText is now a string and has a trim method
    if (!rawText || typeof rawText.trim !== 'function' || rawText.trim() === '') {
      return [];
    }

    let cleanedText = comp.cleanStreamingArtifacts(rawText);

    cleanedText = cleanedText
      .replace(/##+\s*/g, '')   // Remove all markdown headers (# ## ### etc.)
      .replace(/^---+\s*$/gm, '') // Remove horizontal rules without breaking table separators
      .replace(/\n\n+/g, '\n')  // Collapse multiple newlines
      .trim();

    // SPLIT LOGIC: Use a stateful parser to identify points while keeping tables whole
    const lines = cleanedText.split(/\n/);
    let points: string[] = [];
    let currentPoint = '';
    let inTable = false;

    lines.forEach((line: any) => {
      const trimmedLine = line.trim();
      const isTableLine = trimmedLine.startsWith('|');

      if (isTableLine) {
        // If we were building a non-table point, save it and start the table
        if (!inTable && currentPoint.trim() !== '') {
          points.push(currentPoint.trim());
          currentPoint = '';
        }
        inTable = true;
        currentPoint += (currentPoint ? '\n' : '') + line;
      } else {
        // If we're coming out of a table, save the table block first
        if (inTable) {
          points.push(currentPoint.trim());
          currentPoint = '';
          inTable = false;
        }

        // Handle normal point splitting: Start a new point for every non-empty line
        if (trimmedLine !== '') {
          if (currentPoint.trim() !== '') {
            points.push(currentPoint.trim());
          }
          currentPoint = line;
        }
      }
    });

    // Push the final remaining point
    if (currentPoint.trim() !== '') {
      points.push(currentPoint.trim());
    }

    let finalPoints = points
      .map((pointText: string) => {
        // Only clean non-table lines deeply
        if (pointText.includes('|')) {
          return pointText;
        }

        let cleaned = pointText
          .replace(/^[-•*]\s*/, '')  // Remove bullet markers at start of the point
          .replace(/^\s*-\s*/, '')   // Remove hyphens
          .replace(/\s+/g, ' ')       // Collapse multiple internal spaces
          .trim();

        // Ensure each distinct point (if sentence-like) has a period if not ending in punctuation
        if (cleaned && !cleaned.match(/[.!?]$/) && cleaned.length > 20) {
          cleaned += '.';
        }

        return cleaned;
      })
      .filter((p: string) => p.length > 2); // Keep all valid points

    return finalPoints.length > 0 ? finalPoints : ["No Data Available"];
  }


  static queueStatusMessage(comp: any, msg: string) {
    if (msg) {
      comp.statusQueue.push(msg);
      if (!comp.isProcessingStatusQueue) {
        comp.processNextStatus();
      }
    }
  }

  static processNextStatus(comp: any) {
    if (comp.statusQueue.length === 0) {
      comp.isProcessingStatusQueue = false;
      return;
    }

    comp.isProcessingStatusQueue = true;
    const nextMsg = comp.statusQueue.shift();
    if (nextMsg) {
      comp.streamStatusText = nextMsg;
    }

    setTimeout(() => {
      comp.processNextStatus();
    }, 800);
  }
}
